import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { VehicleCard } from "@/components/VehicleCard";
import { MonthlyEarningsChart, MonthlyExpensesChart } from "@/components/RevenueChart";
import { UtilizationChart } from "@/components/UtilizationChart";
import { NetProfitMarginChart } from "@/components/NetProfitMarginChart";
import { CashFlowChart } from "@/components/CashFlowChart";
import { BarChart3 } from "lucide-react";
import { useVehicleMetrics } from "@/hooks/useVehicleMetrics";
import { Skeleton } from "@/components/ui/skeleton";
import analytics from "@/lib/segment";

export default function Dashboard() {
  const [firstName, setFirstName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { vehicleMetrics, isLoading: metricsLoading, hasVehicles } = useVehicleMetrics();

  useEffect(() => {
    // Track dashboard view
    analytics.track("Dashboard Viewed", {
      has_vehicles: hasVehicles,
      vehicle_count: vehicleMetrics?.length || 0,
    });
  }, [hasVehicles, vehicleMetrics?.length]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      setUserEmail(user.email || null);
      
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (data?.full_name) {
        const nameParts = data.full_name.trim().split(" ");
        setFirstName(nameParts[0]);
      }
    };

    fetchUserInfo();
  }, []);

  return (
    <AppLayout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Your Business at a Glance</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back{firstName ? `, ${firstName}` : userEmail ? `, ${userEmail.split("@")[0]}` : ""}
          </p>
        </div>

        {/* Vehicle Performance */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-primary" />
              Vehicle Performance
            </h2>
          </div>
          {metricsLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : hasVehicles && vehicleMetrics.length > 0 ? (
            <div className="space-y-4">
              {vehicleMetrics.map((vehicle) => (
                <VehicleCard key={vehicle.vehicleId} vehicle={vehicle} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No vehicles found. Add vehicles in the Vehicles tab to see performance metrics.</p>
            </div>
          )}
        </div>

        {/* Financial Overview */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
            <BarChart3 className="h-6 w-6 text-primary" />
            Financial Overview
          </h2>
          <div className="grid gap-6 lg:grid-cols-3 mb-6">
            <MonthlyEarningsChart />
            <MonthlyExpensesChart />
            <NetProfitMarginChart />
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <UtilizationChart />
            <CashFlowChart />
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
